// ---- SF2 (SoundFont 2) Binary Writer ---------------------------------------
// Ported from spessasynth_core's SF2 writer (TypeScript) to plain JavaScript.
// Produces valid .sf2 files from a simplified data model:
//   { info, presets, instruments, samples }
//
// Data model:
//   info: { name, engineer, software, copyright, comment, creationDate }
//   samples: [{ name, pcm (Int16Array), sampleRate, loopStart, loopEnd, originalKey, pitchCorrection, sampleType, linkedSampleIndex }]
//   instruments: [{ name, zones: [{ sampleIndex, keyRange:[lo,hi], velRange:[lo,hi], generators: { type: value, ... } }] }]
//   presets: [{ name, bank, program, zones: [{ instrumentIndex, keyRange:[lo,hi], velRange:[lo,hi], generators: {} }] }]
//
// License: Apache-2.0 (original) / MIT (port)

// ---- SF2 Generator Types ----
const GEN = {
   startAddrsOffset: 0,
   endAddrOffset: 1,
   startloopAddrsOffset: 2,
   endloopAddrsOffset: 3,
   startAddrsCoarseOffset: 4,
   modLfoToPitch: 5,
   vibLfoToPitch: 6,
   modEnvToPitch: 7,
   initialFilterFc: 8,
   initialFilterQ: 9,
   modLfoToFilterFc: 10,
   modEnvToFilterFc: 11,
   endAddrsCoarseOffset: 12,
   modLfoToVolume: 13,
   chorusEffectsSend: 15,
   reverbEffectsSend: 16,
   pan: 17,
   delayModLFO: 21,
   freqModLFO: 22,
   delayVibLFO: 23,
   freqVibLFO: 24,
   delayModEnv: 25,
   attackModEnv: 26,
   holdModEnv: 27,
   decayModEnv: 28,
   sustainModEnv: 29,
   releaseModEnv: 30,
   keyNumToModEnvHold: 31,
   keyNumToModEnvDecay: 32,
   delayVolEnv: 33,
   attackVolEnv: 34,
   holdVolEnv: 35,
   decayVolEnv: 36,
   sustainVolEnv: 37,
   releaseVolEnv: 38,
   keyNumToVolEnvHold: 39,
   keyNumToVolEnvDecay: 40,
   instrument: 41,
   keyRange: 43,
   velRange: 44,
   startloopAddrsCoarseOffset: 45,
   keyNum: 46,
   velocity: 47,
   initialAttenuation: 48,
   endloopAddrsCoarseOffset: 50,
   coarseTune: 51,
   fineTune: 52,
   sampleID: 53,
   sampleModes: 54,
   scaleTuning: 56,
   exclusiveClass: 57,
   overridingRootKey: 58,
   endOper: 60,
};

// ---- Binary helpers ----
function writeStr(view, off, str, len) {
   for (let i = 0; i < len; i++) {
      view.setUint8(off + i, i < str.length ? str.charCodeAt(i) & 0xFF : 0);
   }
}

function writeU16LE(view, off, val) {
   view.setUint16(off, val & 0xFFFF, true);
}

function writeU32LE(view, off, val) {
   view.setUint32(off, val >>> 0, true);
}

function writeS16LE(view, off, val) {
   view.setInt16(off, val, true);
}

// ---- RIFF chunk helpers ----
// RIFF structure: [4-byte type][4-byte size][data][pad byte if odd]
// LIST: [4-byte "LIST"][4-byte size][4-byte listType][...sub-chunks]

function makeChunk(type, data) {
   // Returns Uint8Array for a RIFF chunk: type(4) + size(4) + data + pad
   const size = data.length;
   const pad = (size % 2) ? 1 : 0;
   const buf = new Uint8Array(8 + size + pad);
   const view = new DataView(buf.buffer);
   writeStr(view, 0, type, 4);
   writeU32LE(view, 4, size);
   buf.set(data, 8);
   return buf;
}

function makeList(listType, chunks) {
   // Concatenate all sub-chunk data
   let total = 4; // listType
   for (const c of chunks) total += c.length;
   const data = new Uint8Array(total);
   const view = new DataView(data.buffer);
   writeStr(view, 0, listType, 4);
   let off = 4;
   for (const c of chunks) { data.set(c, off); off += c.length; }
   return makeChunk('LIST', data);
}

// ---- INFO chunk ----
function buildInfo(info) {
   const parts = [];
   const addStr = (type, str) => {
      if (!str) return;
      const s = str + '\0'; // null-terminated
      const pad = (s.length % 2) ? 1 : 0;
      const data = new Uint8Array(s.length + pad);
      for (let i = 0; i < s.length; i++) data[i] = s.charCodeAt(i) & 0xFF;
      parts.push(makeChunk(type, data));
   };

   // ifil (version) — always 2.01
   {
      const data = new Uint8Array(4);
      const v = new DataView(data.buffer);
      writeU16LE(v, 0, 2); // major
      writeU16LE(v, 2, 1); // minor
      parts.push(makeChunk('ifil', data));
   }
   addStr('isng', 'EMU8000');
   addStr('INAM', info.name || 'Untitled');
   addStr('IENG', info.engineer || '');
   addStr('IPRD', info.product || '');
   addStr('ICOP', info.copyright || '');
   addStr('ICMT', info.comment || '');
   addStr('ISFT', info.software || 'AZOPSOFT SF2 Export');

   // ICRD — creation date
   const date = info.creationDate || new Date().toISOString().slice(0, 10);
   addStr('ICRD', date);

   return makeList('INFO', parts);
}

// ---- sdta chunk (sample data) ----
function buildSdta(samples) {
   // smpl sub-chunk: raw 16-bit little-endian PCM for each sample,
   // followed by 46 zero samples (92 bytes) after each sample
   const smplParts = [];
   const startOffsets = [];
   const endOffsets = [];

   let byteOffset = 0;
   for (const s of samples) {
      const pcmBytes = s.pcm.length * 2;
      const data = new Uint8Array(pcmBytes + 92); // +46 zero samples
      const view = new DataView(data.buffer);
      for (let i = 0; i < s.pcm.length; i++) {
         view.setInt16(i * 2, s.pcm[i], true);
      }
      smplParts.push(data);

      startOffsets.push(byteOffset / 2); // in sample points
      endOffsets.push((byteOffset + pcmBytes) / 2);
      byteOffset += pcmBytes + 92;
   }

   // Concatenate all smpl parts
   let total = 0;
   for (const p of smplParts) total += p.length;
   const smplData = new Uint8Array(total);
   let off = 0;
   for (const p of smplParts) { smplData.set(p, off); off += p.length; }

   const smpl = makeChunk('smpl', smplData);
   const sdta = makeList('sdta', [smpl]);

   return { sdta, startOffsets, endOffsets };
}

// ---- shdr chunk (sample headers) ----
function buildShdr(samples, startOffsets, endOffsets) {
   const SAMPLE_REC_SIZE = 46;
   const data = new Uint8Array(SAMPLE_REC_SIZE * (samples.length + 1));
   const view = new DataView(data.buffer);
   let off = 0;

   for (let i = 0; i < samples.length; i++) {
      const s = samples[i];
      // Name (20 bytes)
      writeStr(view, off, (s.name || `sample_${i}`).slice(0, 20), 20);
      off += 20;
      // Start (dwStart)
      writeU32LE(view, off, startOffsets[i]); off += 4;
      // End (dwEnd)
      writeU32LE(view, off, endOffsets[i]); off += 4;
      // Loop start (absolute)
      const loopStart = (s.loopStart || 0) + startOffsets[i];
      writeU32LE(view, off, loopStart); off += 4;
      // Loop end (absolute)
      const loopEnd = (s.loopEnd || 0) + startOffsets[i];
      writeU32LE(view, off, loopEnd); off += 4;
      // Sample rate
      writeU32LE(view, off, s.sampleRate || 44100); off += 4;
      // Original key (root pitch)
      view.setUint8(off++, s.originalKey || 60);
      // Pitch correction (signed byte)
      view.setInt8(off++, s.pitchCorrection || 0);
      // Sample link (index of linked sample for stereo, 0 if none)
      writeU16LE(view, off, s.linkedSampleIndex || 0); off += 2;
      // Sample type (1=mono, 2=right, 4=left, 8=link, 0=ROM)
      writeU16LE(view, off, s.sampleType || 1); off += 2;
   }

   // EOS terminal record
   writeStr(view, off, 'EOS', 20); off += 20;
   // Rest is zeros (already zero-filled)

   return makeChunk('shdr', data);
}

// ---- Generator writer ----
// Each generator record: 4 bytes — u16 type, s16 value
function buildGenerators(genList) {
   // genList: array of { type, value }
   // Terminal record: type=60 (endOper), value=0
   const records = [...genList, { type: GEN.endOper, value: 0 }];
   const data = new Uint8Array(records.length * 4);
   const view = new DataView(data.buffer);
   for (let i = 0; i < records.length; i++) {
      writeU16LE(view, i * 4, records[i].type);
      writeS16LE(view, i * 4 + 2, records[i].value);
   }
   return data;
}

// ---- Modulator writer (terminal record only — we don't write custom mods) ----
function buildModulators() {
   // Terminal modulator record: 10 bytes of zeros
   return new Uint8Array(10);
}

// ---- Bag writer ----
// Each bag record: 4 bytes — u16 genIndex, u16 modIndex
function buildBags(bagList) {
   const data = new Uint8Array(bagList.length * 4);
   const view = new DataView(data.buffer);
   for (let i = 0; i < bagList.length; i++) {
      writeU16LE(view, i * 4, bagList[i].genIndex);
      writeU16LE(view, i * 4 + 2, bagList[i].modIndex);
   }
   return data;
}

// ---- phdr (preset headers) ----
function buildPhdr(presets, presetBagIndexes) {
   const PHDR_SIZE = 38;
   const data = new Uint8Array(PHDR_SIZE * (presets.length + 1));
   const view = new DataView(data.buffer);
   let off = 0;

   for (let i = 0; i < presets.length; i++) {
      const p = presets[i];
      writeStr(view, off, (p.name || `Preset ${i}`).slice(0, 20), 20); off += 20;
      writeU16LE(view, off, p.program || 0); off += 2;
      writeU16LE(view, off, p.bank || 0); off += 2;
      writeU16LE(view, off, presetBagIndexes[i]); off += 2;
      // library, genre, morphology (unused, 3 × u32)
      writeU32LE(view, off, 0); off += 4;
      writeU32LE(view, off, 0); off += 4;
      writeU32LE(view, off, 0); off += 4;
   }

   // EOP terminal
   writeStr(view, off, 'EOP', 20); off += 20;
   writeU16LE(view, off, 0); off += 2; // program
   writeU16LE(view, off, 0); off += 2; // bank
   writeU16LE(view, off, presetBagIndexes[presets.length] || 0); off += 2;
   writeU32LE(view, off, 0); off += 4;
   writeU32LE(view, off, 0); off += 4;
   writeU32LE(view, off, 0); off += 4;

   return makeChunk('phdr', data);
}

// ---- inst (instrument headers) ----
function buildInst(instruments, instBagIndexes) {
   const INST_SIZE = 22;
   const data = new Uint8Array(INST_SIZE * (instruments.length + 1));
   const view = new DataView(data.buffer);
   let off = 0;

   for (let i = 0; i < instruments.length; i++) {
      const inst = instruments[i];
      writeStr(view, off, (inst.name || `Inst ${i}`).slice(0, 20), 20); off += 20;
      writeU16LE(view, off, instBagIndexes[i]); off += 2;
   }

   // EOI terminal
   writeStr(view, off, 'EOI', 20); off += 20;
   writeU16LE(view, off, instBagIndexes[instruments.length] || 0); off += 2;

   return makeChunk('inst', data);
}

// ---- Build pdta chunk ----
function buildPdta(bank, startOffsets, endOffsets) {
   const { presets, instruments, samples } = bank;

   // Build bags and generators for presets
   const presetGenParts = [];
   const presetBagList = [];
   const presetBagIndexes = [];
   let genIdx = 0, modIdx = 0, bagIdx = 0;

   for (const p of presets) {
      presetBagIndexes.push(bagIdx);
      // Global zone (if any) — we skip global zones for simplicity
      // Each preset zone
      for (const z of p.zones) {
         const gens = [];
         // Key range (if specified)
         if (z.keyRange) {
            gens.push({ type: GEN.keyRange, value: (z.keyRange[0] & 0xFF) | ((z.keyRange[1] & 0xFF) << 8) });
         }
         // Velocity range (if specified)
         if (z.velRange) {
            gens.push({ type: GEN.velRange, value: (z.velRange[0] & 0xFF) | ((z.velRange[1] & 0xFF) << 8) });
         }
         // Instrument index
         gens.push({ type: GEN.instrument, value: z.instrumentIndex });
         // Extra generators
         if (z.generators) {
            for (const [type, value] of Object.entries(z.generators)) {
               gens.push({ type: parseInt(type), value });
            }
         }
         const genData = buildGenerators(gens);
         presetGenParts.push(genData);
         presetBagList.push({ genIndex: genIdx, modIndex: modIdx });
         genIdx += gens.length + 1; // +1 for terminal
         modIdx += 1; // terminal modulator
         bagIdx++;
      }
   }
   // Terminal bag record (points to terminal generator/modulator)
   presetBagList.push({ genIndex: genIdx, modIndex: modIdx });
   presetBagIndexes.push(bagIdx); // terminal

   // Build bags and generators for instruments
   const instGenParts = [];
   const instBagList = [];
   const instBagIndexes = [];
   genIdx = 0; modIdx = 0; bagIdx = 0;

   for (const inst of instruments) {
      instBagIndexes.push(bagIdx);
      for (const z of inst.zones) {
         const gens = [];
         // Key range
         if (z.keyRange) {
            gens.push({ type: GEN.keyRange, value: (z.keyRange[0] & 0xFF) | ((z.keyRange[1] & 0xFF) << 8) });
         }
         // Velocity range
         if (z.velRange) {
            gens.push({ type: GEN.velRange, value: (z.velRange[0] & 0xFF) | ((z.velRange[1] & 0xFF) << 8) });
         }
         // Sample ID
         gens.push({ type: GEN.sampleID, value: z.sampleIndex });
         // Extra generators
         if (z.generators) {
            for (const [type, value] of Object.entries(z.generators)) {
               gens.push({ type: parseInt(type), value });
            }
         }
         const genData = buildGenerators(gens);
         instGenParts.push(genData);
         instBagList.push({ genIndex: genIdx, modIndex: modIdx });
         genIdx += gens.length + 1;
         modIdx += 1;
         bagIdx++;
      }
   }
   // Terminal bag record
   instBagList.push({ genIndex: genIdx, modIndex: modIdx });
   instBagIndexes.push(bagIdx); // terminal

   // Build chunks
   const phdrChunk = buildPhdr(presets, presetBagIndexes);

   // pbag
   const pbagData = buildBags(presetBagList);
   const pbagChunk = makeChunk('pbag', pbagData);

   // pmod (terminal only)
   const pmodChunk = makeChunk('pmod', buildModulators());

   // pgen — concatenate all generator data
   let pgenTotal = 0;
   for (const g of presetGenParts) pgenTotal += g.length;
   const pgenData = new Uint8Array(pgenTotal);
   let pgenOff = 0;
   for (const g of presetGenParts) { pgenData.set(g, pgenOff); pgenOff += g.length; }
   const pgenChunk = makeChunk('pgen', pgenData);

   // inst
   const instChunk = buildInst(instruments, instBagIndexes);

   // ibag
   const ibagData = buildBags(instBagList);
   const ibagChunk = makeChunk('ibag', ibagData);

   // imod (terminal only)
   const imodChunk = makeChunk('imod', buildModulators());

   // igen
   let igenTotal = 0;
   for (const g of instGenParts) igenTotal += g.length;
   const igenData = new Uint8Array(igenTotal);
   let igenOff = 0;
   for (const g of instGenParts) { igenData.set(g, igenOff); igenOff += g.length; }
   const igenChunk = makeChunk('igen', igenData);

   // shdr
   const shdrChunk = buildShdr(samples, startOffsets, endOffsets);

   return makeList('pdta', [
      phdrChunk, pbagChunk, pmodChunk, pgenChunk,
      instChunk, ibagChunk, imodChunk, igenChunk,
      shdrChunk
   ]);
}

// ---- Main export function ----
export function buildSF2(bank) {
   // bank: { info, presets, instruments, samples }
   const info = bank.info || {};
   const samples = bank.samples || [];
   const presets = bank.presets || [];
   const instruments = bank.instruments || [];

   if (!samples.length) throw new Error('SF2: no samples');
   if (!presets.length) throw new Error('SF2: no presets');
   if (!instruments.length) throw new Error('SF2: no instruments');

   // Build INFO
   const infoChunk = buildInfo(info);

   // Build sdta (sample data)
   const { sdta, startOffsets, endOffsets } = buildSdta(samples);

   // Build pdta (preset/instrument/sample definitions)
   const pdtaChunk = buildPdta({ presets, instruments, samples }, startOffsets, endOffsets);

   // Build final RIFF file
   // RIFF header: "RIFF" + size + "sfbk" + INFO + sdta + pdta
   const riffType = 'sfbk';
   const subChunks = [infoChunk, sdta, pdtaChunk];

   let dataSize = 4; // "sfbk"
   for (const c of subChunks) dataSize += c.length;
   const totalSize = 8 + dataSize; // "RIFF" + size + data
   const buf = new ArrayBuffer(totalSize);
   const view = new DataView(buf);
   const bytes = new Uint8Array(buf);

   writeStr(view, 0, 'RIFF', 4);
   writeU32LE(view, 4, dataSize);
   writeStr(view, 8, riffType, 4);
   let off = 12;
   for (const c of subChunks) { bytes.set(c, off); off += c.length; }

   return bytes;
}

// ---- Helper: create a simple bank from flat sample list ----
// Creates one preset (bank 0, program 0) with one instrument,
// each sample gets its own zone spanning the full key range.
export function createSimpleBank(samples, name) {
   const instrZones = samples.map((s, i) => ({
      sampleIndex: i,
      keyRange: [0, 127],
      velRange: [0, 127],
      generators: s.generators || {},
   }));

   const presetZones = [{
      instrumentIndex: 0,
      keyRange: [0, 127],
      velRange: [0, 127],
      generators: {},
   }];

   return {
      info: { name: name || 'Untitled SoundFont' },
      samples: samples.map(s => ({
         name: (s.name || 'sample').slice(0, 20),
         pcm: s.pcm,
         sampleRate: s.sampleRate || 44100,
         loopStart: s.loopStart || 0,
         loopEnd: s.loopEnd || 0,
         originalKey: s.originalKey || 60,
         pitchCorrection: s.pitchCorrection || 0,
         sampleType: s.sampleType || 1,
         linkedSampleIndex: s.linkedSampleIndex || 0,
      })),
      instruments: [{
         name: (name || 'Instrument').slice(0, 20),
         zones: instrZones,
      }],
      presets: [{
         name: (name || 'Preset').slice(0, 20),
         bank: 0,
         program: 0,
         zones: presetZones,
      }],
   };
}

// Export generator types for UI use
export { GEN };

// ---- SF2 Import Parser -----------------------------------------------------
// Parses a .sf2 file and returns a bank structure matching the export model.

// Generator type name lookup (reverse of GEN)
const GEN_NAMES = {};
for (const [k, v] of Object.entries(GEN)) GEN_NAMES[v] = k;

function readStr(view, off, len) {
   let s = '';
   for (let i = 0; i < len; i++) {
      const c = view.getUint8(off + i);
      if (c === 0) break;
      s += String.fromCharCode(c);
   }
   return s;
}

function parseRIFFChunks(buf, baseOff = 0, endOff = buf.byteLength) {
   const view = new DataView(buf);
   const chunks = [];
   let off = baseOff;
   while (off + 8 <= endOff) {
      const type = readStr(view, off, 4);
      const size = view.getUint32(off + 4, true);
      const dataOff = off + 8;
      if (dataOff + size > endOff) break;
      chunks.push({ type, size, dataOff, end: dataOff + size + (size % 2) });
      off = dataOff + size + (size % 2);
   }
   return chunks;
}

export function parseSF2(buf) {
   const view = new DataView(buf);
   const riffType = readStr(view, 0, 4);
   if (riffType !== 'RIFF') throw new Error('Not a RIFF file');
   const formType = readStr(view, 8, 4);
   if (formType !== 'sfbk') throw new Error('Not an SF2 file (expected sfbk)');

   const topChunks = parseRIFFChunks(buf, 12, buf.byteLength);

   // Find INFO, sdta, pdta
   let infoChunk = null, sdtaChunk = null, pdtaChunk = null;
   for (const c of topChunks) {
      if (c.type !== 'LIST') continue;
      const lt = readStr(view, c.dataOff, 4);
      if (lt === 'INFO') infoChunk = c;
      else if (lt === 'sdta') sdtaChunk = c;
      else if (lt === 'pdta') pdtaChunk = c;
   }

   // ---- Parse INFO ----
   const info = {};
   if (infoChunk) {
      const infoSubs = parseRIFFChunks(buf, infoChunk.dataOff + 4, infoChunk.end);
      for (const c of infoSubs) {
         if (c.type === 'INAM') info.name = readStr(view, c.dataOff, c.size);
         else if (c.type === 'IENG') info.engineer = readStr(view, c.dataOff, c.size);
         else if (c.type === 'ICMT') info.comment = readStr(view, c.dataOff, c.size);
         else if (c.type === 'ICOP') info.copyright = readStr(view, c.dataOff, c.size);
         else if (c.type === 'ISFT') info.software = readStr(view, c.dataOff, c.size);
         else if (c.type === 'ICRD') info.creationDate = readStr(view, c.dataOff, c.size);
      }
   }

   // ---- Parse sdta/smpl ----
   let smplData = null;
   if (sdtaChunk) {
      const sdtaSubs = parseRIFFChunks(buf, sdtaChunk.dataOff + 4, sdtaChunk.end);
      for (const c of sdtaSubs) {
         if (c.type === 'smpl') { smplData = { off: c.dataOff, size: c.size }; break; }
      }
   }

   // ---- Parse pdta ----
   if (!pdtaChunk) throw new Error('No pdta chunk found');
   const pdtaSubs = parseRIFFChunks(buf, pdtaChunk.dataOff + 4, pdtaChunk.end);
   const pdta = {};
   for (const c of pdtaSubs) pdta[c.type] = c;

   // ---- Parse shdr (sample headers) ----
   const samples = [];
   if (pdta.shdr) {
      const shdrOff = pdta.shdr.dataOff;
      const numRecords = pdta.shdr.size / 46;
      for (let i = 0; i < numRecords; i++) {
         const off = shdrOff + i * 46;
         const name = readStr(view, off, 20);
         if (name === 'EOS') break;
         const dwStart = view.getUint32(off + 20, true);
         const dwEnd = view.getUint32(off + 24, true);
         const loopStart = view.getUint32(off + 28, true);
         const loopEnd = view.getUint32(off + 32, true);
         const sampleRate = view.getUint32(off + 36, true);
         const originalKey = view.getUint8(off + 40);
         const pitchCorrection = view.getInt8(off + 41);
         const sampleLink = view.getUint16(off + 42, true);
         const sampleType = view.getUint16(off + 44, true);

         // Extract PCM data from smpl
         let pcm = null;
         if (smplData) {
            const byteStart = dwStart * 2;
            const byteEnd = dwEnd * 2;
            const numSamples = dwEnd - dwStart;
            pcm = new Int16Array(numSamples);
            for (let j = 0; j < numSamples; j++) {
               pcm[j] = view.getInt16(smplData.off + byteStart + j * 2, true);
            }
         }

         samples.push({
            name, pcm, sampleRate,
            loopStart: loopStart - dwStart,
            loopEnd: loopEnd - dwStart,
            originalKey, pitchCorrection,
            sampleType, linkedSampleIndex: sampleLink,
         });
      }
   }

   // ---- Parse inst (instrument headers) ----
   const instruments = [];
   if (pdta.inst) {
      const instOff = pdta.inst.dataOff;
      const numRecords = pdta.inst.size / 22;
      for (let i = 0; i < numRecords; i++) {
         const off = instOff + i * 22;
         const name = readStr(view, off, 20);
         if (name === 'EOI') break;
         const bagIndex = view.getUint16(off + 20, true);
         instruments.push({ name, bagIndex, zones: [] });
      }
   }

   // ---- Parse ibag (instrument bags) ----
   const instBagIndexes = [];
   if (pdta.ibag) {
      const ibagOff = pdta.ibag.dataOff;
      const numRecords = pdta.ibag.size / 4;
      for (let i = 0; i < numRecords; i++) {
         const off = ibagOff + i * 4;
         instBagIndexes.push({
            genIndex: view.getUint16(off, true),
            modIndex: view.getUint16(off + 2, true),
         });
      }
   }

   // ---- Parse igen (instrument generators) ----
   const instGens = [];
   if (pdta.igen) {
      const igenOff = pdta.igen.dataOff;
      const numRecords = pdta.igen.size / 4;
      for (let i = 0; i < numRecords; i++) {
         const off = igenOff + i * 4;
         instGens.push({
            type: view.getUint16(off, true),
            value: view.getInt16(off + 2, true),
         });
      }
   }

   // ---- Build instrument zones from bags+gens ----
   for (let i = 0; i < instruments.length; i++) {
      const inst = instruments[i];
      const nextBagIndex = (i + 1 < instruments.length)
         ? instruments[i + 1].bagIndex : instBagIndexes.length;
      for (let b = inst.bagIndex; b < nextBagIndex; b++) {
         const bag = instBagIndexes[b];
         const nextGenIndex = (b + 1 < instBagIndexes.length)
            ? instBagIndexes[b + 1].genIndex : instGens.length;
         const zoneGens = {};
         let sampleIndex = null, keyRange = null, velRange = null;
         for (let g = bag.genIndex; g < nextGenIndex; g++) {
            const gen = instGens[g];
            if (gen.type === GEN.sampleID) sampleIndex = gen.value;
            else if (gen.type === GEN.keyRange) {
               keyRange = [gen.value & 0xFF, (gen.value >> 8) & 0xFF];
            } else if (gen.type === GEN.velRange) {
               velRange = [gen.value & 0xFF, (gen.value >> 8) & 0xFF];
            } else if (gen.type === GEN.endOper) {
               // terminal
            } else {
               zoneGens[gen.type] = gen.value;
            }
         }
         if (sampleIndex !== null) {
            inst.zones.push({ sampleIndex, keyRange, velRange, generators: zoneGens });
         }
      }
   }

   // ---- Parse phdr (preset headers) ----
   const presets = [];
   if (pdta.phdr) {
      const phdrOff = pdta.phdr.dataOff;
      const numRecords = pdta.phdr.size / 38;
      for (let i = 0; i < numRecords; i++) {
         const off = phdrOff + i * 38;
         const name = readStr(view, off, 20);
         if (name === 'EOP') break;
         const program = view.getUint16(off + 20, true);
         const bank = view.getUint16(off + 22, true);
         const bagIndex = view.getUint16(off + 24, true);
         presets.push({ name, program, bank, bagIndex, zones: [] });
      }
   }

   // ---- Parse pbag (preset bags) ----
   const presBagIndexes = [];
   if (pdta.pbag) {
      const pbagOff = pdta.pbag.dataOff;
      const numRecords = pdta.pbag.size / 4;
      for (let i = 0; i < numRecords; i++) {
         const off = pbagOff + i * 4;
         presBagIndexes.push({
            genIndex: view.getUint16(off, true),
            modIndex: view.getUint16(off + 2, true),
         });
      }
   }

   // ---- Parse pgen (preset generators) ----
   const presGens = [];
   if (pdta.pgen) {
      const pgenOff = pdta.pgen.dataOff;
      const numRecords = pdta.pgen.size / 4;
      for (let i = 0; i < numRecords; i++) {
         const off = pgenOff + i * 4;
         presGens.push({
            type: view.getUint16(off, true),
            value: view.getInt16(off + 2, true),
         });
      }
   }

   // ---- Build preset zones from bags+gens ----
   for (let i = 0; i < presets.length; i++) {
      const pres = presets[i];
      const nextBagIndex = (i + 1 < presets.length)
         ? presets[i + 1].bagIndex : presBagIndexes.length;
      for (let b = pres.bagIndex; b < nextBagIndex; b++) {
         const bag = presBagIndexes[b];
         const nextGenIndex = (b + 1 < presBagIndexes.length)
            ? presBagIndexes[b + 1].genIndex : presGens.length;
         let instrumentIndex = null, keyRange = null, velRange = null;
         const zoneGens = {};
         for (let g = bag.genIndex; g < nextGenIndex; g++) {
            const gen = presGens[g];
            if (gen.type === GEN.instrument) instrumentIndex = gen.value;
            else if (gen.type === GEN.keyRange) {
               keyRange = [gen.value & 0xFF, (gen.value >> 8) & 0xFF];
            } else if (gen.type === GEN.velRange) {
               velRange = [gen.value & 0xFF, (gen.value >> 8) & 0xFF];
            } else if (gen.type === GEN.endOper) {
               // terminal
            } else {
               zoneGens[gen.type] = gen.value;
            }
         }
         if (instrumentIndex !== null) {
            pres.zones.push({ instrumentIndex, keyRange, velRange, generators: zoneGens });
         }
      }
   }

   return { info, samples, instruments, presets };
}

// ---- DLS Export (simplified) -----------------------------------------------
// Writes a minimal DLS level 2 file from the same bank structure as SF2.
// DLS uses RIFF "DLS " form with wvpl (wave pool), ins (instruments), and ptbl.

function buildDLS(bank) {
   const { info, samples, instruments, presets } = bank;
   if (!samples.length) throw new Error('DLS: no samples');

   const parts = [];

   // vers chunk (version 2.0)
   {
      const data = new Uint8Array(8);
      const v = new DataView(data.buffer);
      v.setUint32(0, 2, true); // major
      v.setUint32(4, 0, true); // minor
      parts.push(makeChunk('vers', data));
   }

   // DLSID chunk (unique identifier — use a fixed UUID)
   {
      const data = new Uint8Array(16);
      const v = new DataView(data.buffer);
      // Random UUID bytes
      v.setUint32(0, 0xA1050000, true);
      v.setUint32(4, 0x00005000, true);
      v.setUint32(8, 0x00000000, true);
      v.setUint32(12, 0x00000000, true);
      parts.push(makeChunk('DLSID', data));
   }

   // COLH (collection header) — number of instruments
   {
      const data = new Uint8Array(4);
      const v = new DataView(data.buffer);
      v.setUint32(0, instruments.length, true);
      parts.push(makeChunk('colh', data));
   }

   // ptbl (patch table) — maps preset indices to instruments
   {
      const numPatches = Math.max(presets.length, 128);
      const data = new Uint8Array(8 + numPatches * 4);
      const v = new DataView(data.buffer);
      v.setUint32(0, numPatches, true); // count
      v.setUint32(4, 0, true); // unused
      for (let i = 0; i < numPatches; i++) {
         // Map preset to instrument index (simple: first preset zone's instrument)
         let instIdx = 0;
         if (i < presets.length && presets[i].zones.length) {
            instIdx = presets[i].zones[0].instrumentIndex || 0;
         }
         v.setUint32(8 + i * 4, instIdx, true);
      }
      parts.push(makeChunk('ptbl', data));
   }

   // wvpl (wave pool) — LIST of WAVE chunks
   const waveChunks = [];
   for (let i = 0; i < samples.length; i++) {
      const s = samples[i];
      // WAVE LIST sub-chunks: fmt, data, wsmp
      const waveParts = [];

      // fmt chunk (PCM format)
      {
         const data = new Uint8Array(18);
         const v = new DataView(data.buffer);
         v.setUint16(0, 1, true); // PCM
         v.setUint16(2, 1, true); // mono
         v.setUint32(4, s.sampleRate || 44100, true);
         v.setUint32(8, (s.sampleRate || 44100) * 2, true); // byte rate
         v.setUint16(12, 2, true); // block align
         v.setUint16(14, 16, true); // bits per sample
         v.setUint16(16, 0, true); // cb size
         waveParts.push(makeChunk('fmt ', data));
      }

      // data chunk (raw PCM)
      {
         const pcm = s.pcm;
         const data = new Uint8Array(pcm.length * 2);
         const v = new DataView(data.buffer);
         for (let j = 0; j < pcm.length; j++) v.setInt16(j * 2, pcm[j], true);
         waveParts.push(makeChunk('data', data));
      }

      // wsmp chunk (wave sample info)
      {
         const data = new Uint8Array(32);
         const v = new DataView(data.buffer);
         v.setUint32(0, 32, true); // cb size
         v.setUint16(4, 1, true); // unity note
         v.setUint16(6, s.originalKey || 60, true); // root note
         v.setInt32(8, s.pitchCorrection || 0, true); // fine tune
         v.setInt32(12, 0, true); // gain
         v.setUint32(16, 0, true); // flags
         v.setUint32(20, 0, true); // loop count (0 = no loop)
         // If loop, add loop info (simplified: no loop)
         waveParts.push(makeChunk('wsmp', data));
      }

      waveChunks.push(makeList('wave', waveParts));
   }
   parts.push(makeList('wvpl', waveChunks));

   // lins (list of instruments) — LIST of ins chunks
   const insChunks = [];
   for (let i = 0; i < instruments.length; i++) {
      const inst = instruments[i];
      const insParts = [];

      // DLSID for instrument
      {
         const data = new Uint8Array(16);
         const v = new DataView(data.buffer);
         v.setUint32(0, 0xA1050001 + i, true);
         insParts.push(makeChunk('DLSID', data));
      }

      // insh (instrument header)
      {
         const data = new Uint8Array(12);
         const v = new DataView(data.buffer);
         v.setUint32(0, inst.zones.length, true); // region count
         v.setUint32(4, 0, true); // bank
         v.setUint32(8, 0, true); // instrument patch
         insParts.push(makeChunk('insh', data));
      }

      // lrgn (list of regions) — one per zone
      const rgnChunks = [];
      for (const z of inst.zones) {
         const rgnParts = [];

         // rgnh (region header) — key/vel ranges
         {
            const data = new Uint8Array(10);
            const v = new DataView(data.buffer);
            v.setUint16(0, z.keyRange ? z.keyRange[0] : 0, true);
            v.setUint16(2, z.keyRange ? z.keyRange[1] : 127, true);
            v.setUint16(4, z.velRange ? z.velRange[0] : 0, true);
            v.setUint16(6, z.velRange ? z.velRange[1] : 127, true);
            v.setUint16(8, 0, true); // options
            rgnParts.push(makeChunk('rgnh', data));
         }

         // wlnk (wave link) — points to wave in pool
         {
            const data = new Uint8Array(12);
            const v = new DataView(data.buffer);
            v.setUint16(0, 0, true); // flags
            v.setUint16(2, 0, true); // phase group
            v.setUint32(4, z.sampleIndex || 0, true); // wave index
            rgnParts.push(makeChunk('wlnk', data));
         }

         // art2 (articulator) — generators as connection blocks
         if (z.generators && Object.keys(z.generators).length > 0) {
            const connBlocks = [];
            // Convert SF2 generators to DLS connection blocks
            const genToConn = {
               [GEN.pan]: { dst: 0x0010, transform: 0 }, // PAN
               [GEN.initialAttenuation]: { dst: 0x0001, transform: 0 }, // ATTENUATION
               [GEN.coarseTune]: { dst: 0x0003, transform: 0 }, // TUNE (coarse)
               [GEN.fineTune]: { dst: 0x0003, transform: 0 }, // TUNE (fine)
               [GEN.delayVolEnv]: { dst: 0x0005, transform: 0 }, // EG_DELAY_TIME
               [GEN.attackVolEnv]: { dst: 0x0006, transform: 0 }, // EG_ATTACK_TIME
               [GEN.holdVolEnv]: { dst: 0x0007, transform: 0 }, // EG_HOLD_TIME
               [GEN.decayVolEnv]: { dst: 0x0008, transform: 0 }, // EG_DECAY_TIME
               [GEN.sustainVolEnv]: { dst: 0x0009, transform: 0 }, // EG_SUSTAIN_LEVEL
               [GEN.releaseVolEnv]: { dst: 0x000A, transform: 0 }, // EG_RELEASE_TIME
               [GEN.initialFilterFc]: { dst: 0x0002, transform: 0 }, // FILTER_CUTOFF
               [GEN.initialFilterQ]: { dst: 0x0004, transform: 0 }, // FILTER_Q
            };
            for (const [type, value] of Object.entries(z.generators)) {
               const conn = genToConn[parseInt(type)];
               if (conn) {
                  connBlocks.push({ src: 0, ctrl: 0, dst: conn.dst, transform: conn.transform, scale: value });
               }
            }
            const numConns = connBlocks.length;
            const artData = new Uint8Array(12 + numConns * 16);
            const v = new DataView(artData.buffer);
            v.setUint32(0, 12, true); // cb size
            v.setUint32(4, numConns, true); // connection count
            v.setUint32(8, 0, true); // unused
            for (let c = 0; c < numConns; c++) {
               const cb = connBlocks[c];
               const off = 12 + c * 16;
               v.setUint16(off, cb.src, true);
               v.setUint16(off + 2, cb.ctrl, true);
               v.setUint16(off + 4, cb.dst, true);
               v.setUint16(off + 6, cb.transform, true);
               v.setInt32(off + 8, cb.scale, true);
            }
            rgnParts.push(makeChunk('art2', artData));
         }

         rgnChunks.push(makeList('rgn ', rgnParts));
      }
      insParts.push(makeList('lrgn', rgnChunks));

      insChunks.push(makeList('ins ', insParts));
   }
   parts.push(makeList('lins', insChunks));

   // Build final RIFF DLS file
   const subChunks = parts;
   let dataSize = 4; // "DLS "
   for (const c of subChunks) dataSize += c.length;
   const buf = new ArrayBuffer(8 + dataSize);
   const view = new DataView(buf);
   const bytes = new Uint8Array(buf);
   writeStr(view, 0, 'RIFF', 4);
   writeU32LE(view, 4, dataSize);
   writeStr(view, 8, 'DLS ', 4);
   let off = 12;
   for (const c of subChunks) { bytes.set(c, off); off += c.length; }
   return bytes;
}

// ---- SF3 Export (compressed) -----------------------------------------------
// SF3 uses OGG Vorbis compression for sample data.
// This is a stub — full implementation requires an OGG Vorbis encoder (WASM).
// The file structure is identical to SF2 except smpl contains Vorbis packets
// and shdr has the SF3 bit flag set on sampleType.

export function buildSF3(bank) {
   throw new Error('SF3 export requires OGG Vorbis encoder (not yet integrated). Use SF2 export instead.');
}

export { buildDLS, parseRIFFChunks, GEN_NAMES };
